import { useCallback } from "react";
import useStore from "../helpers/useStore";
import {
  MutationDraftContext,
  useMutationScope,
} from "../stores/MutationDraft/mutationDraft.provider";
import { AgentChatContext, useChatScope } from "../stores/AgentChat/agentChat.provider";
import { MutationDraft, MutationScope } from "../stores/MutationDraft/types";
import { useCalendarMutationAdapter } from "./useCalendarMutationAdapter";

/**
 * Orchestration hook for accept / reject / regenerate actions on calendar drafts.
 *
 * This is the single point of entry for user interactions with mutation drafts.
 * Scope is resolved from MutationScopeContext/ChatScopeContext if not passed explicitly.
 * Posts chat messages on each action for audit trail / UX feedback.
 */
export const useCalendarDraftActions = (scopeOverride?: MutationScope) => {
  const mutationScope = useMutationScope();
  const chatScope = useChatScope();
  const scope = scopeOverride ?? mutationScope;

  const registry = useStore(MutationDraftContext);
  const draftStore = registry.getStore(scope);
  const chatRegistry = useStore(AgentChatContext);
  const chatStore = chatRegistry.getStore(chatScope);
  const { regenerate: regenerateDraft, isReady } = useCalendarMutationAdapter(scope);

  /**
   * Accept a draft — keeps it in the store as "accepted".
   * The actual save to backend is handled separately by the caller
   * because different sources have different save APIs.
   */
  const accept = useCallback(
    (draftId: string) => {
      const entry = draftStore.getEntry(draftId);
      if (!entry) return;

      chatStore.add({
        role: "assistant",
        content: `✓ Angenommen: ${entry.request.label}`,
        timestamp: new Date(),
      });
    },
    [draftStore, chatStore],
  );

  /**
   * Reject a draft — removes it from the store and stores the rejection reason.
   * Returns the removed draft so the UI can show it in a "rejected" state.
   */
  const reject = useCallback(
    (draftId: string, reason?: string): MutationDraft | undefined => {
      const draft = draftStore.getDraft(draftId);
      if (!draft) return undefined;

      // Read label BEFORE removing the entry
      const label = draftStore.getRequest(draftId)?.label ?? draftId;

      draftStore.removeDraft(draftId);
      if (reason) {
        draftStore.setRejection(draftId, reason);
      }

      chatStore.add({
        role: "user",
        content: reason ? `✗ Abgelehnt: ${label}. Grund: ${reason}` : `✗ Abgelehnt: ${label}`,
        timestamp: new Date(),
      });

      return draft;
    },
    [draftStore, chatStore],
  );

  /**
   * Regenerate a single draft — re-runs the generator for the original request.
   * Clears the rejection and replaces with a new draft.
   */
  const regenerate = useCallback(
    (draftId: string): MutationDraft | null => {
      const request = draftStore.getRequest(draftId);
      const label = request?.label ?? draftId;

      const newDraft = regenerateDraft(draftId);

      if (newDraft) {
        chatStore.add({
          role: "user",
          content: `🔄 Neu berechnet: ${label}`,
          timestamp: new Date(),
        });
      } else {
        chatStore.add({
          role: "system",
          content: `Konnte keinen neuen Vorschlag für "${label}" finden.`,
          timestamp: new Date(),
        });
      }

      return newDraft;
    },
    [draftStore, chatStore, regenerateDraft],
  );

  /**
   * Regenerate all rejected drafts at once.
   * Returns the count of successfully regenerated drafts.
   */
  const regenerateAllRejected = useCallback((): number => {
    const rejectedIds = draftStore.rejectedIds;

    if (rejectedIds.length === 0) {
      chatStore.add({
        role: "system",
        content: "Keine abgelehnten Termine zum Neu-Berechnen.",
        timestamp: new Date(),
      });
      return 0;
    }

    let count = 0;
    for (const id of rejectedIds) {
      const result = regenerate(id);
      if (result) count++;
    }

    return count;
  }, [draftStore, chatStore, regenerate]);

  /**
   * Clear all drafts and rejections for this scope (full reset).
   */
  const clearAll = useCallback(() => {
    registry.clearScope(scope);
    chatStore.clear();
  }, [registry, chatStore, scope]);

  return {
    accept,
    reject,
    regenerate,
    regenerateAllRejected,
    clearAll,
    isReady,
    /** Convenience: check if there are any rejections pending */
    hasRejections: draftStore.hasRejections,
    /** Convenience: get rejected IDs */
    rejectedIds: draftStore.rejectedIds,
  };
};
